import {
  Box,
  Grid,
  GridItem,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
  Flex,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import xhr from 'xhr';
import Loading from 'components/loading/Loading';
import useAuth from 'hooks/useAuth';
import StudentInfo from './components/StudentInfo';
import StudentPrograms from './components/StudentPrograms';
import SchoolPagosTab from './components/SchoolPagosTab';
import SchoolRequisitosTab from './components/SchoolRequisitosTab';
import CreateEnrollmentDrawer from './components/CreateEnrollmentDrawer';
import EnrollmentsTable from 'pages/administrador/ver-estudiante/[id]/components/enrollmentsTable';
import CareerTable from 'pages/administrador/ver-estudiante/[id]/components/careerTable';
import CourseTable from 'pages/administrador/ver-estudiante/[id]/components/courseTable';

async function getSchoolStudentInfo(data) {
  const { id_est, user } = data;

  const dataStudent = await xhr(
    `/students?filters[id][$eq]=${id_est}&filters[schoolFrom][id][$eq]=${user.school?.id}&populate=deep,3`
  );

  if (dataStudent.data.data.length === 0) return { error: 'No se encontro el estudiante' };

  const resProgramTypes = await xhr(`/program-types/student/${id_est}`);

  // Check if school has semesters and groups configured
  const semRes = await xhr(
    `/semesters?filters[school][id][$eq]=${user.school?.id}&pagination[limit]=1`
  );
  const groupRes = await xhr(
    `/class-groups?filters[school][id][$eq]=${user.school?.id}&pagination[limit]=1`
  );

  // Check enrollment types
  const gradeEnrollments = await xhr(
    `/grade-enrollments?filters[student][id][$eq]=${id_est}&pagination[limit]=1`
  );
  const techEnrollments = await xhr(
    `/technical-career-enrollments?filters[student][id][$eq]=${id_est}&pagination[limit]=1`
  );
  const courseEnrollments = await xhr(
    `/course-enrollments?filters[student][id][$eq]=${id_est}&pagination[limit]=1`
  );

  return {
    estudiante: dataStudent.data.data[0],
    programTypes: resProgramTypes.data,
    hasSchoolSemesters: (semRes.data?.data?.length || 0) > 0,
    hasSchoolGroups: (groupRes.data?.data?.length || 0) > 0,
    hasGradeEnrollments: (gradeEnrollments.data?.data?.length || 0) > 0,
    hasTechEnrollments: (techEnrollments.data?.data?.length || 0) > 0,
    hasCourseEnrollments: (courseEnrollments.data?.data?.length || 0) > 0,
  };
}

const tabStyle = {
  _focus: { boxShadow: 'none' },
  _selected: {
    borderBottom: '2px solid #574feb',
    fontWeight: 'bold',
    color: '#574feb',
  },
};

export default function SchoolVerEstudiante() {
  const params = useParams();
  const navigate = useNavigate();
  const id_est = params.id;
  const { user } = useAuth();

  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();

  const { data, mutate } = useSWR(
    { id_est, user, type: 'getSchoolStudentInfo' },
    getSchoolStudentInfo,
    { revalidateOnFocus: false }
  );

  if (!data) return <Loading />;
  if (data.error) return <Box p={8}>{data.error}</Box>;

  const {
    estudiante,
    programTypes,
    hasSchoolSemesters,
    hasSchoolGroups,
    hasGradeEnrollments,
    hasTechEnrollments,
    hasCourseEnrollments,
  } = data;

  const needsConfig = !hasSchoolSemesters || !hasSchoolGroups;

  return (
    <Box>
      <Flex align="center" mb={5}>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<FiArrowLeft />}
          onClick={() => navigate('/plataforma/school/estudiantes')}
          mr={3}
        >
          Volver
        </Button>
        <Heading size="lg">
          {estudiante.user?.first_name} {estudiante.user?.last_name}
        </Heading>
      </Flex>

      {needsConfig && (
        <Alert status="warning" borderRadius="lg" mb={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {!hasSchoolSemesters && !hasSchoolGroups
              ? 'No tienes periodos academicos ni grupos configurados. '
              : !hasSchoolSemesters
              ? 'No tienes periodos academicos configurados. '
              : 'No tienes grupos configurados. '}
            Es necesario configurarlos para poder asignarlos a los estudiantes.{' '}
            <Link to="/plataforma/school/configuracion">
              <Button size="xs" colorScheme="purple" variant="link" ml={1}>
                Ir a Configuracion
              </Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <Grid templateColumns={{ base: '1fr', md: '280px 1fr' }} gap={5}>
        {/* Left sidebar: Student info */}
        <GridItem>
          <StudentInfo estudiante={estudiante} />
        </GridItem>

        {/* Main content */}
        <GridItem>
          {/* Programs section */}
          <StudentPrograms
            programTypes={programTypes}
            studentId={id_est}
            onAddProgram={onDrawerOpen}
          />

          <CreateEnrollmentDrawer
            isOpen={isDrawerOpen}
            onClose={onDrawerClose}
            studentId={id_est}
            onSuccess={() => mutate()}
          />

          {/* Tabs */}
          <Box
            mt={5}
            bg="white"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
          >
            <Tabs isLazy>
              <TabList px={4} pt={2}>
                <Tab {...tabStyle}>Pagos</Tab>
                {programTypes?.length > 0 && <Tab {...tabStyle}>Requisitos</Tab>}
                {hasGradeEnrollments && <Tab {...tabStyle}>Grados</Tab>}
                {hasTechEnrollments && <Tab {...tabStyle}>Carreras Tecnicas</Tab>}
                {hasCourseEnrollments && <Tab {...tabStyle}>Cursos</Tab>}
              </TabList>
              <TabPanels>
                <TabPanel>
                  <SchoolPagosTab studentId={id_est} schoolId={user?.school?.id} />
                </TabPanel>
                {programTypes?.length > 0 && (
                  <TabPanel>
                    <SchoolRequisitosTab
                      studentId={id_est}
                      schoolId={user?.school?.id}
                      programTypes={programTypes}
                    />
                  </TabPanel>
                )}
                {hasGradeEnrollments && (
                  <TabPanel>
                    <EnrollmentsTable />
                  </TabPanel>
                )}
                {hasTechEnrollments && (
                  <TabPanel>
                    <CareerTable />
                  </TabPanel>
                )}
                {hasCourseEnrollments && (
                  <TabPanel>
                    <CourseTable />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}
